// ── Events Page ───────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { api } from '../api';
import './Contact.css';

export function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getEvents().then(setEvents).finally(() => setLoading(false));
  }, []);

  const fmt = (dateStr, timeStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const date = d.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
    if (!timeStr) return date;
    const [h, m] = timeStr.split(':');
    const ampm = +h >= 12 ? 'PM' : 'AM';
    const hr   = +h % 12 || 12;
    return `${date} at ${hr}:${m} ${ampm}`;
  };

  return (
    <div>
      <div style={{ background:'var(--navy)', padding:'120px 0 56px', textAlign:'center' }}>
        <div className="container">
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4vw,3rem)', color:'var(--white)', marginBottom:12 }}>Upcoming Events</h1>
          <p style={{ color:'rgba(255,255,255,0.7)', fontSize:'1.05rem' }}>Adoption events, fundraisers, and community gatherings across New Mexico</p>
        </div>
      </div>

      <div className="container section">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : events.length === 0 ? (
          <div style={{ textAlign:'center', padding:'64px 0' }}>
            <div style={{ fontSize:'4rem', marginBottom:16 }}>📅</div>
            <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', marginBottom:8 }}>No upcoming events</h3>
            <p style={{ color:'var(--text-light)' }}>Check back soon! We host regular adoption events across New Mexico.</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:24, maxWidth:800 }}>
            {events.map(ev => (
              <div key={ev.id} className="card" style={{ display:'flex', overflow:'hidden' }}>
                {ev.photo && (
                  <div style={{ width:200, flexShrink:0 }}>
                    <img src={`https://runarounddogtown.com/rescue-api${ev.photo}`} alt={ev.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>
                )}
                <div style={{ padding:28, flex:1 }}>
                  <div style={{ fontFamily:'var(--font-ui)', fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--red)', marginBottom:8 }}>
                    📅 {fmt(ev.event_date, ev.event_time)}
                  </div>
                  <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', color:'var(--navy)', marginBottom:8 }}>{ev.title}</h3>
                  {ev.location_name && <p style={{ fontFamily:'var(--font-ui)', fontSize:'0.875rem', color:'var(--text-light)', marginBottom:12 }}>📍 {ev.location_name}{ev.location_address ? ` — ${ev.location_address}` : ''}</p>}
                  {ev.description   && <p style={{ fontSize:'0.9rem', color:'var(--text)', lineHeight:1.6, marginBottom:16 }}>{ev.description}</p>}
                  {ev.external_url  && <a href={ev.external_url} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ fontSize:'0.85rem', padding:'8px 20px' }}>More Info →</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── About Page ────────────────────────────────────────────────
export function About() {
  return (
    <div>
      <div style={{ background:'var(--navy)', padding:'120px 0 56px', textAlign:'center' }}>
        <div className="container">
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4vw,3rem)', color:'var(--white)', marginBottom:12 }}>About Us</h1>
          <p style={{ fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'1.2rem', color:'var(--gold-light)' }}>A Cause for Healing Paws</p>
        </div>
      </div>

      <div className="container section">
        <div style={{ maxWidth:800, margin:'0 auto' }}>
          <h2 className="section-title">Our Mission</h2>
          <p style={{ fontSize:'1.05rem', lineHeight:1.9, marginBottom:32 }}>
            Run Around Dog Town Rescue is a volunteer-run organization based in New Mexico dedicated to rescuing dogs in need and finding them loving forever homes. We believe every dog deserves a second chance, no matter their background, age, or circumstance.
          </p>
          <p style={{ fontSize:'1.05rem', lineHeight:1.9, marginBottom:32 }}>
            Our rescue operates entirely on the hearts and hands of passionate volunteers — people who show up every day because they believe in the healing power of the human-animal bond. From fostering to transport, veterinary coordination to adoption counseling, our team does it all out of pure love for dogs.
          </p>

          <div className="paw-divider"><span>🐾</span></div>

          <h2 className="section-title" style={{ marginTop:40 }}>What We Do</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:24, margin:'32px 0' }}>
            {[
              { icon:'🚗', title:'Rescue & Transport',   desc:'We rescue dogs from shelters and difficult situations across New Mexico.' },
              { icon:'🏠', title:'Foster Care',          desc:'Dogs live in loving foster homes while they wait for their forever family.' },
              { icon:'🩺', title:'Veterinary Care',      desc:'Every dog receives full medical care, vaccinations, and spay/neuter.' },
              { icon:'❤️', title:'Adoption Matching',    desc:'We carefully match each dog with the right family for a lifelong bond.' },
            ].map(item => (
              <div key={item.title} style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:24, textAlign:'center' }}>
                <div style={{ fontSize:'2.5rem', marginBottom:12 }}>{item.icon}</div>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', color:'var(--navy)', marginBottom:8 }}>{item.title}</h3>
                <p style={{ fontSize:'0.875rem', color:'var(--text-light)', lineHeight:1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="paw-divider"><span>🐾</span></div>

          <h2 className="section-title" style={{ marginTop:40 }}>Meet the Team</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:24, margin:'32px 0' }}>
            {[
              { name:'Chris', phone:'575-770-8688', role:'Rescue Coordinator' },
              { name:'Sharon', phone:'575-779-7721', role:'Adoption Coordinator' },
            ].map(person => (
              <div key={person.name} style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:28, textAlign:'center' }}>
                <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--cream-dark)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.8rem', margin:'0 auto 12px' }}>🐾</div>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', color:'var(--navy)', marginBottom:4 }}>{person.name}</h3>
                <p style={{ fontFamily:'var(--font-ui)', fontSize:'0.75rem', color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>{person.role}</p>
                <a href={`tel:${person.phone.replace(/-/g,'')}`} style={{ fontFamily:'var(--font-ui)', fontSize:'0.9rem', color:'var(--red)', fontWeight:600 }}>{person.phone}</a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Donate Page ───────────────────────────────────────────────
export function Donate() {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.getSettings().then(setSettings); }, []);

  return (
    <div>
      <div style={{ background:'var(--navy)', padding:'120px 0 56px', textAlign:'center' }}>
        <div className="container">
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4vw,3rem)', color:'var(--white)', marginBottom:12 }}>Support Our Rescue</h1>
          <p style={{ color:'rgba(255,255,255,0.7)', fontSize:'1.05rem' }}>Every dollar goes directly toward caring for rescued dogs in New Mexico</p>
        </div>
      </div>

      <div className="container section">
        <div style={{ maxWidth:700, margin:'0 auto', textAlign:'center' }}>
          <div style={{ fontSize:'4rem', marginBottom:24 }}>❤️</div>
          <h2 className="section-title">Your Gift Saves Lives</h2>
          <p style={{ fontSize:'1.05rem', lineHeight:1.9, marginBottom:32, color:'var(--text)' }}>
            Run Around Dog Town Rescue operates entirely through donations and the dedication of our volunteers. Your support pays for veterinary care, food, supplies, and transport for every dog in our program.
          </p>

          {settings.donation_url ? (
            <a href={settings.donation_url} target="_blank" rel="noreferrer" className="btn btn-gold" style={{ fontSize:'1.1rem', padding:'16px 40px' }}>
              Donate Now
            </a>
          ) : (
            <div style={{ background:'var(--cream-dark)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:32 }}>
              <p style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', marginBottom:16 }}>To make a donation, please contact us directly:</p>
              <a href="mailto:RunAroundDogTown@gmail.com" className="btn btn-primary">RunAroundDogTown@gmail.com</a>
            </div>
          )}

          <div className="paw-divider" style={{ marginTop:48 }}><span>🐾</span></div>

          <h3 className="section-title" style={{ marginTop:40 }}>Other Ways to Help</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:20, marginTop:32 }}>
            {[
              { icon:'🏠', title:'Foster a Dog',    desc:'Open your home temporarily while a dog waits for adoption.' },
              { icon:'🚗', title:'Volunteer',       desc:'Help with transport, events, or administrative support.' },
              { icon:'📢', title:'Spread the Word', desc:'Share our dogs on social media to help them find homes faster.' },
              { icon:'🛍️', title:'Supply Donations', desc:'Food, bedding, leashes, and toys are always needed.' },
            ].map(item => (
              <div key={item.title} style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:20, textAlign:'center' }}>
                <div style={{ fontSize:'2rem', marginBottom:8 }}>{item.icon}</div>
                <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', color:'var(--navy)', marginBottom:6 }}>{item.title}</h4>
                <p style={{ fontSize:'0.8rem', color:'var(--text-light)', lineHeight:1.5 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Contact Page ──────────────────────────────────────────────
export function Contact() {
  const [form, setForm]           = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.contact(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ background:'var(--navy)', padding:'120px 0 56px', textAlign:'center' }}>
        <div className="container">
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4vw,3rem)', color:'var(--white)', marginBottom:12 }}>Contact Us</h1>
          <p style={{ color:'rgba(255,255,255,0.7)', fontSize:'1.05rem' }}>We'd love to hear from you</p>
        </div>
      </div>

      <div className="container section">
        <div className="contact-grid">
          <div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', color:'var(--navy)', marginBottom:24 }}>Get in Touch</h2>
            {[
              { icon:'📧', label:'Email',  value:'RunAroundDogTown@gmail.com', href:'mailto:RunAroundDogTown@gmail.com' },
              { icon:'📞', label:'Chris',  value:'575-770-8688', href:'tel:5757708688' },
              { icon:'📞', label:'Sharon', value:'575-779-7721', href:'tel:5757797721' },
              { icon:'📍', label:'Location', value:'New Mexico', href:null },
            ].map(item => (
              <div key={item.label} style={{ display:'flex', gap:16, marginBottom:20, alignItems:'flex-start' }}>
                <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--cream-dark)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', flexShrink:0 }}>{item.icon}</div>
                <div>
                  <div style={{ fontFamily:'var(--font-ui)', fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-light)', marginBottom:2 }}>{item.label}</div>
                  {item.href
                    ? <a href={item.href} style={{ color:'var(--red)', fontWeight:600 }}>{item.value}</a>
                    : <span style={{ color:'var(--text)' }}>{item.value}</span>
                  }
                </div>
              </div>
            ))}

            <div style={{ marginTop:32, padding:20, background:'var(--cream-dark)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)' }}>
              <p style={{ fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'1rem', color:'var(--navy)', lineHeight:1.6 }}>
                "A Cause for Healing Paws" — we're here to help connect dogs with the families they deserve.
              </p>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="alert alert-success">
                🐾 Thank you for reaching out! We'll get back to you as soon as possible.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && <div className="alert alert-error">{error}</div>}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-control" required value={form.name} onChange={e=>f('name',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-control" type="email" required value={form.email} onChange={e=>f('email',e.target.value)} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" type="tel" value={form.phone} onChange={e=>f('phone',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input className="form-control" value={form.subject} onChange={e=>f('subject',e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea className="form-control" required rows={6} value={form.message} onChange={e=>f('message',e.target.value)} />
                </div>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Memorial Wall ─────────────────────────────────────────────
export function Memorial() {
  const [memorialDogs,  setMemorialDogs]  = useState([]);
  const [rescueDogs,    setRescueDogs]    = useState([]);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      api.getMemorial(),                    // dedicated memorial_dogs table
      api.getDogs('status=&in_memorial=1'), // dogs flagged in_memorial
    ]).then(([mem, dogs]) => {
      setMemorialDogs(mem);
      // filter dogs that have in_memorial set
      setRescueDogs(dogs.filter(d => d.in_memorial == 1));
    }).finally(() => setLoading(false));
  }, []);

  const allEntries = [
    // Dogs from the dogs table marked in_memorial
    ...rescueDogs.map(d => ({
      id:         `dog-${d.id}`,
      name:       d.name,
      breed:      d.breed,
      photo:      d.primary_photo,
      story:      d.description,
      date_passed:null,
      adopted_by: null,
      fromDogs:   true,
    })),
    // Dedicated memorial entries
    ...memorialDogs.map(d => ({ ...d, id: `mem-${d.id}`, fromDogs: false })),
  ];

  return (
    <div>
      <div style={{ background:'var(--navy)', padding:'120px 0 56px', textAlign:'center' }}>
        <div className="container">
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4vw,3rem)', color:'var(--white)', marginBottom:12 }}>Memorial Wall</h1>
          <p style={{ fontFamily:'var(--font-display)', fontStyle:'italic', color:'var(--gold-light)', fontSize:'1.1rem' }}>Honoring the beloved dogs who found their forever homes</p>
        </div>
      </div>

      <div className="container section">
        <div style={{ maxWidth:700, margin:'0 auto 48px', textAlign:'center' }}>
          <p style={{ fontSize:'1.05rem', lineHeight:1.9, color:'var(--text)' }}>
            This wall is dedicated to the memory of the wonderful dogs who passed through our rescue and into the hearts of their families. Though they are gone, they are never forgotten.
          </p>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : allEntries.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-light)' }}>
            <p>No memorial entries yet.</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:28 }}>
            {allEntries.map(dog => (
              <div key={dog.id} className="card" style={{ textAlign:'center' }}>
                <div style={{ height:220, overflow:'hidden', background:'var(--cream-dark)' }}>
                  {dog.photo
                    ? <img
                        src={`https://runarounddogtown.com/rescue-api${dog.photo}`}
                        alt={dog.name}
                        style={{ width:'100%', height:'100%', objectFit:'cover', filter:'grayscale(100%)' }}
                      />
                    : <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem' }}>🌈</div>
                  }
                </div>
                <div style={{ padding:24 }}>
                  <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', color:'var(--navy)', marginBottom:4 }}>{dog.name}</h3>
                  {dog.breed && <p style={{ fontFamily:'var(--font-ui)', fontSize:'0.8rem', color:'var(--text-light)', marginBottom:8 }}>{dog.breed}</p>}
                  {dog.date_passed && (
                    <p style={{ fontFamily:'var(--font-ui)', fontSize:'0.8rem', color:'var(--red)', marginBottom:12 }}>
                      🌹 {new Date(dog.date_passed + 'T12:00:00').toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })}
                    </p>
                  )}
                  {dog.adopted_by && <p style={{ fontSize:'0.85rem', color:'var(--text-light)', marginBottom:8, fontStyle:'italic' }}>Beloved by the {dog.adopted_by} family</p>}
                  {dog.story && <p style={{ fontSize:'0.875rem', color:'var(--text)', lineHeight:1.6 }}>{dog.story}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
